/**
 * Small presentational building blocks shared by every insight drill-down
 * view. They encapsulate the spacing, typography, and delta styling so each
 * bespoke view stays declarative.
 */
import { createContext, useContext, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { formatLaneKey, extractLaneLocodes } from "@/lib/formatPort";
import { formatPersonName } from "@/lib/formatPersonName";
import { cn } from "@/lib/utils";
import { typeLoneTitle, typeOverline, typeScale } from "@/lib/typography";
import {
  SensitiveInlineText,
  SensitiveMetaValue,
  SensitiveText,
} from "@/components/common/Sensitive";

import { barWidthPct, formatBeforeAfter, formatDeltaPct } from "./formatInsight";

const InsightFlushContext = createContext(false);

/** Drop the inline-expansion chrome when an insight is shown in a modal. */
export function InsightFlush({ children }: { children: ReactNode }) {
  return (
    <InsightFlushContext.Provider value={true}>{children}</InsightFlushContext.Provider>
  );
}

/** Outer wrapper with the divider, spacing, and optional explainer sentence. */
export function InsightShell({
  explainer,
  children,
  className,
}: {
  explainer?: string;
  children: ReactNode;
  className?: string;
}) {
  const flush = useContext(InsightFlushContext);
  return (
    <div
      className={cn(
        "space-y-2.5",
        typeScale.label,
        flush ? "space-y-3" : "mt-2 border-t border-white/5 pt-2",
        className,
      )}
    >
      {explainer && (
        <p className={cn(typeScale.label, "leading-body text-muted-foreground")}>
          {explainer}
        </p>
      )}
      {children}
    </div>
  );
}

/** A subtle "This month vs last month" chip. */
export function PeriodBanner({ label }: { label: string }) {
  return (
    <p
      className={cn(
        typeOverline,
        "inline-flex rounded-full bg-white/5 px-2 py-0.5 font-medium text-muted-foreground/80",
      )}
    >
      {label}
    </p>
  );
}

export interface MetricItem {
  label: string;
  value: string;
}

/** Compact KPI chips. Monetary values are masked when enabled. */
export function MetricGrid({ items }: { items: MetricItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="grid grid-cols-2 gap-1.5">
      {items.map((m) => (
        <li
          key={m.label}
          className="rounded-lg bg-white/[0.04] px-2 py-1.5"
        >
          <div className="flex min-w-0 items-center justify-between gap-2">
            <p className={cn("min-w-0 truncate text-left text-caption text-muted-foreground")}>
              {m.label}
            </p>
            <SensitiveMetaValue
              label={m.label}
              value={m.value}
              className="shrink-0 text-right text-label font-semibold tabular-nums"
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** A titled section wrapping a list of rows. */
export function EntitySection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className={cn(typeLoneTitle, "text-muted-foreground")}>
        {title}
      </p>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

/**
 * An entity name, rendered as a deep link when an href is provided. When
 * `party` is set the name is treated as PII and fully blurred under privacy
 * masking; otherwise only embedded money / contract tokens are masked.
 */
function FormattedLaneName({
  laneKey,
  href,
}: {
  laneKey: string;
  href?: string | null;
}) {
  const { preferences } = useUserPreferences();
  const portNames = usePortNames(extractLaneLocodes(laneKey));
  const label = formatLaneKey(laneKey, preferences.showLocodes, portNames);
  return <EntityName text={label} href={href} />;
}

export function EntityName({
  text,
  href,
  party = false,
}: {
  text: string;
  href?: string | null;
  party?: boolean;
}) {
  const display = party ? formatPersonName(text) : text;
  const content = party ? (
    <SensitiveText value={display} />
  ) : (
    <SensitiveInlineText text={display} />
  );
  if (href) {
    return (
      <Link
        to={href}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
      >
        {content}
        <ArrowUpRight className="size-2.5" />
      </Link>
    );
  }
  return <span className="font-medium">{content}</span>;
}

export type InsightBar =
  | { type: "compare"; prior: number; current: number; unit?: string; max: number }
  | { type: "share"; value: number; max: number }
  | { type: "ratio"; value: number; total: number };

function CompareTrack({
  prior,
  current,
  max,
  down,
}: {
  prior: number;
  current: number;
  max: number;
  down: boolean;
}) {
  return (
    <div className="insight-compare-track" aria-hidden>
      <span
        className="insight-compare-track__prior"
        style={{ width: `${barWidthPct(prior, max)}%` }}
      />
      <span
        className={cn(
          "insight-compare-track__current",
          down ? "insight-compare-track__current--down" : "insight-compare-track__current--up",
        )}
        style={{ width: `${barWidthPct(current, max)}%` }}
      />
    </div>
  );
}

function ShareTrack({
  value,
  max,
  tone = "neutral",
}: {
  value: number;
  max: number;
  tone?: "neutral" | "down" | "up";
}) {
  return (
    <div className="insight-compare-track" aria-hidden>
      <span
        className={cn(
          "insight-compare-track__current",
          tone === "down" && "insight-compare-track__current--down",
          tone === "up" && "insight-compare-track__current--up",
        )}
        style={{ width: `${barWidthPct(value, max)}%` }}
      />
    </div>
  );
}

/** One row: entity, a visual track, and a highlighted value. */
export function ChangeRow({
  name,
  href,
  party = false,
  lane = false,
  primary,
  primaryClassName,
  subtitle,
  bar,
  deltaPct,
}: {
  name: string;
  href?: string | null;
  party?: boolean;
  /** Treat `name` as a locode lane key (`CNNGB→ILASH`) and format port names. */
  lane?: boolean;
  primary: string;
  primaryClassName?: string;
  subtitle?: string;
  bar?: InsightBar;
  deltaPct?: number;
}) {
  const delta = deltaPct !== undefined ? formatDeltaPct(deltaPct) : null;
  const down = (deltaPct ?? 0) < 0;
  const label = (
    lane ? (
      <FormattedLaneName laneKey={name} href={null} />
    ) : (
      <EntityName text={name} href={null} party={party} />
    )
  );
  const value = (
    <span
      className={cn(
        "shrink-0 text-right text-xs font-semibold tabular-nums",
        delta?.className ?? primaryClassName ?? "text-muted-foreground",
      )}
    >
      <SensitiveInlineText text={delta?.text ?? primary} />
    </span>
  );
  const caption =
    bar?.type === "compare"
      ? formatBeforeAfter(bar.prior, bar.current, bar.unit ?? "")
      : subtitle;
  const hoverCaption =
    bar?.type === "compare" && subtitle && subtitle !== caption ? subtitle : undefined;

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "flex min-w-0 items-center gap-0.5 truncate",
            href && "text-primary",
          )}
        >
          {label}
          {href && (
            <ArrowUpRight className="insight-compare-row__go size-2.5 shrink-0" />
          )}
        </span>
        {value}
      </div>
      {bar?.type === "compare" && (
        <CompareTrack
          prior={bar.prior}
          current={bar.current}
          max={bar.max}
          down={down}
        />
      )}
      {bar?.type === "share" && (
        <ShareTrack value={bar.value} max={bar.max} tone={down ? "down" : "neutral"} />
      )}
      {bar?.type === "ratio" && (
        <ShareTrack
          value={bar.value}
          max={bar.total > 0 ? bar.total : 1}
          tone="neutral"
        />
      )}
      {caption && (
        <p className="text-[10px] leading-tight text-muted-foreground">
          <SensitiveInlineText text={caption} />
          {hoverCaption && (
            <span className="insight-compare-row__extra">
              {" · "}
              <SensitiveInlineText text={hoverCaption} />
            </span>
          )}
        </p>
      )}
    </>
  );

  return (
    <li>
      {href ? (
        <Link
          to={href}
          onClick={(e) => e.stopPropagation()}
          className="insight-compare-row"
        >
          {body}
        </Link>
      ) : (
        <div className="insight-compare-row insight-compare-row--static">{body}</div>
      )}
    </li>
  );
}

/** A pill-style call-to-action link. */
export function CtaLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
    >
      {children}
      <ArrowUpRight className="size-2.5" />
    </Link>
  );
}
